// Import root app
import {
  ChakraProvider,
  ColorModeScript,
  createLocalStorageManager,
  LightMode,
} from '@chakra-ui/react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { Fragment, lazy, ReactElement, ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import ErrorPage from '../../../../pages/Error';
import { UserSettingsProvider } from './UserSettingsProvider/UserSettingsProvider';
// Initialize other services and configs
import { GOOGLE_CLIENT_ID } from '../../../../auth/config';
import { onError } from '../../../../pages/Error/helpers';
import theme from '../../../../theme';
import { environments } from '../../../config';
import '../../../config/dateTime';
import { storage } from '../../../services/storage';
import PostHogSetup from './PostHogSetup/PostHogSetup';
import { queryClient } from './queryClient';
import AppSetup from './Setup/Setup';

const DevTools = lazy(() => import('./DevTools/DevTools'));

const localStoragePersister = createSyncStoragePersister({
  storage,
});

persistQueryClient({
  queryClient,
  persister: localStoragePersister,
});

export interface ProvidersProps {
  children: ReactNode;
}

const key = 'wr';
const manager = createLocalStorageManager(key);

const GProvider = GOOGLE_CLIENT_ID
  ? (props: { children: ReactNode }) => (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || ''} {...props} />
    )
  : (props: { children: ReactNode }) => <Fragment {...props} />;

const Providers = ({ children }: ProvidersProps): ReactElement | null => (
  <PostHogSetup>
    <BrowserRouter
      future={{
        v7_relativeSplatPath: true,
        v7_startTransition: true,
      }}
    >
      <GProvider>
        <UserSettingsProvider>
          <ColorModeScript initialColorMode={theme.config.initialColorMode} storageKey={key} />
          <ChakraProvider resetCSS theme={theme} colorModeManager={manager}>
            <LightMode>
              <HelmetProvider>
                <QueryClientProvider client={queryClient}>
                  <ErrorBoundary FallbackComponent={ErrorPage} onError={onError}>
                    {children}
                  </ErrorBoundary>
                  <AppSetup />
                  {!environments.isProd && <DevTools />}
                </QueryClientProvider>
              </HelmetProvider>
            </LightMode>
          </ChakraProvider>
        </UserSettingsProvider>
      </GProvider>
    </BrowserRouter>
  </PostHogSetup>
);
export default Providers;
